<template>
  <div class="flex h-full w-full justify-center p-3 md:p-5">
    <div class="flex w-full max-w-3xl max-h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
      <header class="flex items-start justify-between gap-3 bg-gradient-to-br from-blue-50 to-white px-4 py-3">
        <div class="flex min-w-0 flex-1 flex-col gap-2">
          <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <p class="text-xs font-semibold uppercase tracking-widest text-blue-600">Aperçu du sondage</p>
            <div v-if="contentUuid || canExportStats" class="inline-flex flex-wrap items-center justify-end gap-2">
              <button
                v-if="canExportStats"
                type="button"
                class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-blue-600 px-2.5 py-1 text-xs font-semibold leading-none text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="isExportingStats"
                @click="handleStatsExport"
              >
                <i
                  :class="isExportingStats ? 'fas fa-spinner fa-spin' : 'fas fa-file-export'"
                  aria-hidden="true"
                ></i>
                <span>{{ isExportingStats ? 'Export en cours…' : 'Exporter les statistiques' }}</span>
              </button>
              <button
                v-if="contentUuid"
                type="button"
                class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold leading-none text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                :class="{
                  'border-green-200 bg-green-50 text-green-700': uuidCopyStatus === 'copied',
                  'border-red-300 bg-red-100 text-red-700': uuidCopyStatus === 'error'
                }"
                :title="`Copier l'UUID complet : ${contentUuid}`"
                :aria-label="`Copier l'UUID complet ${contentUuid}`"
                @click="copyContentUuid"
              >
                <span>UUID: {{ truncatedUuid }}</span>
                <i
                  class="fas"
                  :class="uuidCopyStatus === 'copied' ? 'fa-check' : (uuidCopyStatus === 'error' ? 'fa-exclamation-circle' : 'fa-copy')"
                  aria-hidden="true"
                ></i>
              </button>
              <span
                class="text-xs font-semibold"
                :class="uuidCopyStatus === 'error' ? 'text-red-700' : 'text-green-700'"
                role="status"
                aria-live="polite"
              >{{ uuidCopyMessage }}</span>
            </div>
          </div>
          <h3 class="text-lg font-semibold leading-snug text-gray-900">{{ displayTitle }}</h3>
        </div>
        <button
          type="button"
          class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-blue-600 transform transition hover:-translate-y-0.5 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          @click="close"
          aria-label="Fermer l'aperçu"
        >
          <i class="fas fa-times text-sm"></i>
        </button>
      </header>

      <div class="flex flex-1 overflow-y-auto px-4 py-5">
        <div v-if="loading" class="mx-auto flex flex-col items-center gap-2 text-xs text-gray-500">
          <i class="fas fa-spinner fa-spin text-blue-500"></i>
          <span>Chargement de l'aperçu…</span>
        </div>

        <div v-else-if="error" class="mx-auto flex max-w-sm flex-col items-center gap-1.5 text-center text-xs text-red-600">
          <i class="fas fa-triangle-exclamation"></i>
          <span>{{ error }}</span>
        </div>

        <div v-else-if="previewGroup" class="flex w-full flex-col gap-3.5">
          <div
            v-if="canViewResults"
            class="flex flex-wrap items-center rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm"
          >
            <div v-if="resultsLoading" class="inline-flex items-center gap-2 text-xs text-gray-500">
              <i class="fas fa-spinner fa-spin text-blue-500"></i>
              <span>Chargement des résultats…</span>
            </div>

            <div v-else-if="resultsError" class="inline-flex items-center gap-2 text-xs text-red-600">
              <i class="fas fa-triangle-exclamation"></i>
              <span>{{ resultsError }}</span>
            </div>

            <template v-else-if="results">
              <div class="flex min-w-0 flex-1 items-center gap-3 pr-4">
                <span class="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <i class="fas fa-users"></i>
                </span>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-widest text-gray-500">Réponses totales</p>
                  <p class="text-2xl font-semibold leading-tight text-gray-900">{{ results.respondents }}</p>
                  <p class="text-xs text-gray-400">sur {{ results.eligible_users }} téléchargements</p>
                </div>
              </div>

              <div class="flex min-w-0 flex-1 items-center gap-3 border-l border-gray-200 pl-4">
                <span class="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <i class="fas fa-chart-simple"></i>
                </span>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-widest text-gray-500">Taux de réponse</p>
                  <p class="text-2xl font-semibold leading-tight text-gray-900">{{ formatPercentage(results.response_rate) }}</p>
                  <p class="text-xs text-gray-400">{{ results.respondents }} / {{ results.eligible_users }}</p>
                </div>
              </div>
            </template>
          </div>

          <ul class="flex flex-col gap-1 text-xs text-gray-600">
            <li v-if="scheduleLabel" class="inline-flex items-center gap-1.5">
              <i class="fas fa-calendar-alt text-blue-500"></i>
              <span>{{ scheduleLabel }}</span>
            </li>
            <li v-if="groupLabels.length" class="inline-flex items-center gap-1.5">
              <i class="fas fa-users text-blue-500"></i>
              <span>{{ associatedUserGroupsLabel }}</span>
            </li>
          </ul>

          <p v-if="previewGroup.description" class="text-sm leading-relaxed text-gray-700">
            {{ previewGroup.description }}
          </p>

          <div v-if="questionList.length" class="flex flex-col gap-3">
            <section
              v-for="(question, index) in questionList"
              :key="question.uuid || `question-${index}`"
              class="flex flex-col gap-2.5 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm"
            >
              <div>
                <span class="text-xs font-semibold uppercase tracking-widest text-blue-500">
                  Question {{ index + 1 }}
                </span>
                <p class="mt-1 text-base font-semibold text-gray-900">
                  {{ question.question }}
                </p>
              </div>

              <p v-if="question.description" class="text-sm leading-relaxed text-gray-600">
                {{ question.description }}
              </p>

              <div v-if="questionBadges(question).length" class="flex flex-wrap gap-1">
                <span
                  v-for="badge in questionBadges(question)"
                  :key="badge"
                  class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                >
                  {{ badge }}
                </span>
              </div>

              <div v-if="questionResults(question)" class="flex flex-col gap-3">
                <div
                  v-for="(option, optionIndex) in questionResults(question).options"
                  :key="option.uuid || `result-${optionIndex}`"
                  class="flex flex-col gap-1.5"
                >
                  <div class="flex items-baseline justify-between gap-3">
                    <span class="flex min-w-0 items-baseline gap-2">
                      <span class="text-xs text-gray-400">{{ optionIndex + 1 }}</span>
                      <span class="text-sm text-gray-700">{{ option.option }}</span>
                    </span>
                    <span class="flex flex-shrink-0 items-baseline gap-3">
                      <span class="text-sm font-semibold text-gray-900">{{ option.vote_count }}</span>
                      <span class="w-14 text-right text-sm font-semibold text-blue-600">
                        {{ formatPercentage(option.percentage) }}
                      </span>
                      <span
                        v-if="option.custom_answers.length"
                        class="inline-flex items-center gap-1 text-xs text-gray-400"
                        :title="`${option.custom_answers.length} réponse(s) libre(s)`"
                      >
                        <i class="fas fa-file-lines" aria-hidden="true"></i>
                        <span>{{ option.custom_answers.length }}</span>
                      </span>
                    </span>
                  </div>

                  <div class="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                    <div class="h-1.5 rounded-full bg-blue-600" :style="{ width: barWidth(option.percentage) }"></div>
                  </div>

                  <div
                    v-if="option.custom_answers.length"
                    class="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
                  >
                    <h5 class="mb-1 text-xs font-semibold uppercase tracking-widest text-gray-400">
                      Réponses libres
                    </h5>
                    <ul class="flex flex-col gap-1">
                      <li
                        v-for="(customAnswer, customIndex) in visibleCustomAnswers(question, option)"
                        :key="`custom-${customIndex}`"
                        class="border-l-2 border-gray-300 pl-2 text-sm text-gray-600"
                      >
                        {{ customAnswer }}
                      </li>
                    </ul>
                    <button
                      v-if="option.custom_answers.length > customAnswersPreviewSize"
                      type="button"
                      class="mt-1 cursor-pointer text-xs font-semibold text-blue-600 hover:text-blue-700 focus:outline-none"
                      @click="toggleCustomAnswers(question, option)"
                    >
                      {{ isCustomAnswersExpanded(question, option)
                        ? 'Réduire'
                        : `Afficher les ${option.custom_answers.length} réponses libres` }}
                    </button>
                  </div>
                </div>

                <div class="flex flex-wrap gap-x-4 gap-y-1 border-t border-gray-100 pt-2 text-xs text-gray-500">
                  <span>{{ questionResults(question).respondents }} répondants</span>
                  <span>{{ formatPercentage(questionResults(question).share_of_total) }} participation</span>
                </div>
              </div>

              <div v-else-if="question.options.length" class="rounded-md bg-blue-50 px-3 py-2">
                <h4 class="mb-1 text-xs font-semibold text-blue-700">Réponses proposées</h4>
                <ol class="list-decimal space-y-1 pl-5 text-sm text-gray-700">
                  <li
                    v-for="(option, optionIndex) in question.options"
                    :key="option.uuid || `option-${optionIndex}`"
                  >
                    {{ option.label }}
                  </li>
                </ol>
              </div>

              <div v-else class="text-sm text-gray-500">
                Aucune réponse n'est définie pour cette question.
              </div>
            </section>
          </div>

          <div v-else class="text-sm text-gray-500">
            Aucune question n'est disponible pour ce sondage.
          </div>
        </div>

        <div v-else class="mx-auto flex flex-col items-center gap-2 text-xs text-gray-500">
          <span>Aucune donnée à afficher.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { isCustomAnswerOption } from '@/utils/surveyOptions'
import { http } from '@/services/api.service'
import { resolveAssociatedUserGroupLabels } from '@/utils/associatedUserGroups'

const ensureArray = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean)
  }
  if (!value) {
    return []
  }
  if (typeof value === 'object') {
    return Object.values(value).filter(Boolean)
  }
  return [value]
}

const parseDateSafe = (value) => {
  if (!value) return null
  try {
    return typeof value === 'string' ? parseISO(value) : value
  } catch (_) {
    try {
      const normalized = typeof value === 'string' ? value.replace(' ', 'T') : value
      const fallback = new Date(normalized)
      return Number.isNaN(fallback.getTime()) ? null : fallback
    } catch (e) {
      return null
    }
  }
}

const selectBoundaryDate = (values, mode) => {
  const timestamps = ensureArray(values)
    .map(parseDateSafe)
    .filter(Boolean)
    .map((date) => date.getTime())

  if (!timestamps.length) {
    return null
  }

  const time = mode === 'max' ? Math.max(...timestamps) : Math.min(...timestamps)
  return new Date(time).toISOString()
}

const coerceBoolean = (value) => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true'
  return false
}

const mapOptions = (options, ownerUuid) => ensureArray(options).map((option, index) => {
  const label = option?.option || option?.label || option?.title
  if (!label) {
    return null
  }
  return {
    uuid: option.uuid || `${ownerUuid || 'option'}-${index}`,
    label
  }
}).filter(Boolean)

const hasCustomAnswer = (options) => ensureArray(options).some((option) => isCustomAnswerOption(option))

export default {
  name: 'SurveyPreviewModal',
  props: {
    surveyUuid: {
      type: String,
      required: true
    },
    surveyGroupUuid: {
      type: String,
      default: null
    },
    initialTitle: {
      type: String,
      default: ''
    },
    source: {
      type: String,
      default: 'pending'
    },
    canExportStats: {
      type: Boolean,
      default: false
    },
    canViewResults: {
      type: Boolean,
      default: false
    },
    exportStats: {
      type: Function,
      default: null
    }
  },
  data () {
    return {
      loading: false,
      error: null,
      previewGroup: null,
      requestToken: 0,
      previewCache: {},
      listCache: {
        domain: null,
        maviepro: null
      },
      isExportingStats: false,
      uuidCopyStatus: 'idle',
      uuidCopyTimer: null,
      results: null,
      resultsLoading: false,
      resultsError: null,
      resultsToken: 0,
      expandedCustomAnswers: {},
      customAnswersPreviewSize: 5
    }
  },
  computed: {
    applicationSlug () {
      const application = this.$store.getters.application
      return application?.app?.slug || null
    },
    displayTitle () {
      return this.previewGroup?.title || this.initialTitle || 'Sondage'
    },
    contentUuid () {
      const uuid = this.previewGroup?.uuid || this.surveyGroupUuid || this.surveyUuid
      return uuid ? String(uuid) : ''
    },
    truncatedUuid () {
      return this.contentUuid.length > 16
        ? `${this.contentUuid.slice(0, 14)}…`
        : this.contentUuid
    },
    uuidCopyMessage () {
      if (this.uuidCopyStatus === 'copied') {
        return 'Copié'
      }
      if (this.uuidCopyStatus === 'error') {
        return 'Copie impossible'
      }
      return ''
    },
    groupLabels () {
      return ensureArray(this.previewGroup?.groups)
    },
    associatedUserGroupsLabel () {
      const prefix = this.groupLabels.length > 1 ? 'Groupes utilisateurs associés' : 'Groupe utilisateur associé'
      return `${prefix} : ${this.groupLabels.join(', ')}`
    },
    scheduleLabel () {
      const startDate = parseDateSafe(this.previewGroup?.start_at || this.previewGroup?.publication_date)
      const endDate = parseDateSafe(this.previewGroup?.end_at)
      if (startDate && endDate) {
        return `Disponible du ${format(startDate, 'dd/MM/yyyy HH:mm')} au ${format(endDate, 'dd/MM/yyyy HH:mm')}`
      }
      if (startDate) {
        return `Débute le ${format(startDate, 'dd/MM/yyyy HH:mm')}`
      }
      if (endDate) {
        return `Expire le ${format(endDate, 'dd/MM/yyyy HH:mm')}`
      }
      return null
    },
    questionList () {
      return ensureArray(this.previewGroup?.surveys)
    },
    resultsByQuestionUuid () {
      const index = {}
      ensureArray(this.results?.questions).forEach((question) => {
        if (question?.uuid) {
          index[String(question.uuid)] = question
        }
      })
      return index
    }
  },
  watch: {
    surveyUuid: 'reload',
    surveyGroupUuid: 'reload',
    source: 'reload',
    contentUuid () {
      this.resetUuidCopyStatus()
    }
  },
  created () {
    this.reload()
  },
  beforeDestroy () {
    this.clearUuidCopyTimer()
  },
  methods: {
    reload () {
      this.fetchSurvey()
      this.fetchResults()
    },
    async fetchResults () {
      this.results = null
      this.resultsError = null
      this.expandedCustomAnswers = {}

      if (!this.canViewResults) {
        return
      }

      const endpoint = this.surveyGroupUuid
        ? `/surveys/${this.surveyGroupUuid}/groupresults`
        : (this.surveyUuid ? `/surveys/${this.surveyUuid}/results` : null)

      if (!endpoint) {
        return
      }

      const token = ++this.resultsToken
      this.resultsLoading = true

      try {
        const { data } = await http.get(endpoint)
        if (this.resultsToken !== token) {
          return
        }
        this.results = data && Array.isArray(data.questions) ? data : null
      } catch (error) {
        if (this.resultsToken === token) {
          this.results = null
          this.resultsError = 'Impossible de charger les résultats de ce sondage.'
        }
      } finally {
        if (this.resultsToken === token) {
          this.resultsLoading = false
        }
      }
    },
    questionResults (question) {
      if (!question?.uuid) {
        return null
      }
      return this.resultsByQuestionUuid[String(question.uuid)] || null
    },
    formatPercentage (value) {
      const percentage = Number(value)
      return `${(Number.isFinite(percentage) ? percentage : 0).toFixed(1)}%`
    },
    barWidth (value) {
      const percentage = Number(value)
      const bounded = Math.max(0, Math.min(100, Number.isFinite(percentage) ? percentage : 0))
      return `${bounded}%`
    },
    customAnswersKey (question, option) {
      return `${question?.uuid || 'question'}:${option?.uuid || option?.option || 'option'}`
    },
    isCustomAnswersExpanded (question, option) {
      return Boolean(this.expandedCustomAnswers[this.customAnswersKey(question, option)])
    },
    visibleCustomAnswers (question, option) {
      const customAnswers = ensureArray(option?.custom_answers)
      return this.isCustomAnswersExpanded(question, option)
        ? customAnswers
        : customAnswers.slice(0, this.customAnswersPreviewSize)
    },
    toggleCustomAnswers (question, option) {
      const key = this.customAnswersKey(question, option)
      this.$set(this.expandedCustomAnswers, key, !this.expandedCustomAnswers[key])
    },
    async handleStatsExport () {
      if (!this.canExportStats || !this.exportStats || this.isExportingStats) {
        return
      }

      this.isExportingStats = true
      try {
        await this.exportStats()
      } finally {
        this.isExportingStats = false
      }
    },
    isGroupVisibleForApplication (group) {
      const slug = this.applicationSlug
      if (!slug || !['maviepro', 'tract'].includes(slug)) {
        return true
      }
      const visibility = (group && group.media_mvp_visibility) || 'both'
      if (!visibility || visibility === 'both') {
        return true
      }
      return visibility === slug
    },
    filterGroupsByApplication (groups) {
      if (!Array.isArray(groups)) {
        return []
      }
      return groups.filter((group) => this.isGroupVisibleForApplication(group))
    },
    async fetchSurvey () {
      const token = ++this.requestToken
      const identifiers = Array.from(new Set([this.surveyGroupUuid, this.surveyUuid].filter(Boolean)))

      if (!identifiers.length) {
        this.previewGroup = null
        this.error = 'Identifiant du sondage manquant.'
        return
      }

      this.loading = true
      this.error = null

      try {
        let preview = this.lookupCache(identifiers)
        if (!preview) {
          preview = this.source === 'maviepro'
            ? await this.resolveMavieproPreview(identifiers)
            : await this.resolveDomainPreview(identifiers)
        }

        if (this.requestToken !== token) {
          return
        }

        if (preview) {
          this.previewGroup = preview
        } else {
          this.previewGroup = null
          this.error = 'Aperçu indisponible pour ce sondage.'
        }
      } catch (error) {
        if (this.requestToken === token) {
          this.previewGroup = null
          this.error = this.source === 'maviepro'
            ? 'Impossible de récupérer ce sondage Maviepro pour le moment.'
            : 'Impossible de charger ce sondage pour le moment.'
        }
      } finally {
        if (this.requestToken === token) {
          this.loading = false
        }
      }
    },
    lookupCache (identifiers) {
      for (const identifier of identifiers) {
        const cached = this.previewCache[String(identifier)]
        if (cached && this.isGroupVisibleForApplication(cached)) {
          return cached
        }
      }
      return null
    },
    async resolveDomainPreview (identifiers) {
      await this.ensureDomainList()
      let preview = this.lookupCache(identifiers)
      if (preview) {
        return preview
      }

      for (const identifier of identifiers) {
        preview = await this.fetchSingleSurvey(identifier)
        if (preview) {
          return preview
        }
      }
      return null
    },
    async resolveMavieproPreview (identifiers) {
      for (const identifier of identifiers) {
        const preview = await this.fetchMavieproDirect(identifier)
        if (preview) {
          return preview
        }
      }
      await this.ensureMavieproList()
      return this.lookupCache(identifiers)
    },
    async ensureDomainList () {
      if (Array.isArray(this.listCache.domain)) {
        return
      }
      const response = await http.get('/domain/surveys', { params: { multiple: true, past: true } })
      const entries = Array.isArray(response?.data) ? response.data : []
      this.listCache.domain = entries
      entries.forEach((entry) => {
        const normalized = this.normalizeSurveyGroup(entry)
        if (normalized) {
          this.cachePreview(normalized)
        }
      })
    },
    async ensureMavieproList () {
      if (Array.isArray(this.listCache.maviepro)) {
        return
      }
      const response = await http.get('/surveys/surveys-mvp')
      const entries = Array.isArray(response?.data) ? response.data : []
      const filteredEntries = this.filterGroupsByApplication(entries)
      this.listCache.maviepro = filteredEntries
      filteredEntries.forEach((entry) => {
        const normalized = this.normalizeMavieproGroup(entry)
        if (normalized) {
          this.cachePreview(normalized)
        }
      })
    },
    async fetchMavieproDirect (uuid) {
      if (!uuid) return null
      const cached = this.lookupCache([uuid])
      if (cached) return cached
      try {
        const response = await http.get(`/surveys/surveys-mvp/${uuid}`)
        const normalized = this.normalizeMavieproGroup(response?.data)
        if (normalized && this.isGroupVisibleForApplication(normalized)) {
          this.cachePreview(normalized)
          return normalized
        }
        return null
      } catch (error) {
        return null
      }
    },
    async fetchSingleSurvey (uuid) {
      if (!uuid) return null
      const cached = this.lookupCache([uuid])
      if (cached) return cached
      const response = await http.get(`/surveys/${uuid}`).catch(() => null)
      const data = response?.data
      if (!data) {
        return null
      }
      const normalized = this.normalizeSurveyGroup({
        uuid: data.uuid,
        group_title: data.question,
        start_at: data.start_at,
        end_at: data.end_at,
        publication_date: data.send_notification_at || data.start_at,
        is_notified: data.is_notified,
        description: data.description,
        groups: data.groups,
        surveys: [data]
      })
      if (normalized) {
        this.cachePreview(normalized)
      }
      return normalized
    },
    normalizeMavieproGroup (entry) {
      if (!entry) return null
      const clone = { ...entry }
      clone.surveys = ensureArray(entry.surveys).map((survey) => ({ ...survey }))
      clone.description = entry.description || entry.group_description || ''
      clone.group_title = entry.group_title || clone.surveys[0]?.question || this.initialTitle || 'Sondage'
      return this.normalizeSurveyGroup(clone)
    },
    normalizeSurveyGroup (entry) {
      if (!entry) return null

      const sourceSurveys = ensureArray(entry.surveys)
      const surveys = sourceSurveys
        .map((survey) => this.normalizeSurvey(survey))
        .filter(Boolean)

      if (!surveys.length) {
        const single = this.normalizeSurvey(entry)
        if (single) {
          surveys.push(single)
        }
      }

      if (!surveys.length) {
        return null
      }

      const title = entry.group_title || entry.title || surveys[0].question || this.initialTitle || 'Sondage'
      const description = entry.description || surveys.find((survey) => survey.description)?.description || ''
      const startAt = entry.start_at || selectBoundaryDate(surveys.map((survey) => survey.start_at), 'min')
      const endAt = entry.end_at || selectBoundaryDate(surveys.map((survey) => survey.end_at), 'max')
      const publicationDate = entry.publication_date || entry.send_notification_at || startAt
      const groups = resolveAssociatedUserGroupLabels(
        entry.group_labels,
        entry.groups,
        ensureArray(entry.notifications).map(notification => notification?.group),
        sourceSurveys.flatMap(survey => ensureArray(survey?.groups)),
        sourceSurveys.flatMap(survey => ensureArray(survey?.notifications).map(notification => notification?.group))
      )

      return {
        uuid: entry.uuid || surveys[0].uuid || null,
        title,
        description,
        start_at: startAt,
        end_at: endAt,
        publication_date: publicationDate,
        is_notified: coerceBoolean(entry.is_notified) || surveys.some((survey) => survey.is_notified),
        groups,
        surveys,
        media_mvp_visibility: entry.media_mvp_visibility || 'both'
      }
    },
    normalizeSurvey (survey) {
      if (!survey) return null
      const options = mapOptions(survey.options, survey.uuid)
      return {
        uuid: survey.uuid || null,
        question: survey.question || survey.title || survey.group_title || 'Question',
        description: survey.description || survey.content || '',
        allows_custom_answer: hasCustomAnswer(survey.options),
        multiple_answers: Boolean(survey.multiple_answers),
        is_reanswerable: Boolean(survey.is_reanswerable),
        is_required: Boolean(survey.is_required),
        is_notified: coerceBoolean(survey.is_notified),
        start_at: survey.start_at || survey.publication_date || null,
        end_at: survey.end_at || null,
        options
      }
    },
    questionBadges (question) {
      if (!question) return []
      const badges = []
      if (question.multiple_answers) badges.push('Plusieurs réponses possibles')
      if (question.allows_custom_answer) badges.push('Réponse personnalisée autorisée')
      if (question.is_reanswerable) badges.push('Réponse modifiable')
      if (question.is_required) badges.push('Réponse obligatoire')
      return badges
    },
    cachePreview (preview) {
      if (!preview) return
      const store = (key) => {
        if (key) {
          this.$set(this.previewCache, String(key), preview)
        }
      }
      store(preview.uuid)
      ensureArray(preview.surveys).forEach((survey) => store(survey.uuid))
    },
    async copyContentUuid () {
      if (!this.contentUuid) {
        return
      }

      try {
        if (navigator.clipboard && window.isSecureContext) {
          try {
            await navigator.clipboard.writeText(this.contentUuid)
          } catch (error) {
            this.copyWithFallback(this.contentUuid)
          }
        } else {
          this.copyWithFallback(this.contentUuid)
        }
        this.setUuidCopyStatus('copied')
      } catch (error) {
        this.setUuidCopyStatus('error')
      }
    },
    copyWithFallback (value) {
      const textarea = document.createElement('textarea')
      textarea.value = value
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      let copied = false
      try {
        textarea.select()
        copied = document.execCommand('copy')
      } finally {
        document.body.removeChild(textarea)
      }
      if (!copied) {
        throw new Error('Clipboard copy failed')
      }
    },
    setUuidCopyStatus (status) {
      this.clearUuidCopyTimer()
      this.uuidCopyStatus = status
      this.uuidCopyTimer = window.setTimeout(() => {
        this.uuidCopyStatus = 'idle'
        this.uuidCopyTimer = null
      }, 2500)
    },
    resetUuidCopyStatus () {
      this.clearUuidCopyTimer()
      this.uuidCopyStatus = 'idle'
    },
    clearUuidCopyTimer () {
      if (this.uuidCopyTimer !== null) {
        window.clearTimeout(this.uuidCopyTimer)
        this.uuidCopyTimer = null
      }
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>
